// Tap a stage on the map: what it has left tonight, in the Sets page's own rows (Sam, 2026-10-06: "much like it does on the
// sets page for that specific column"). The set on now leads with its progress; the rest follow in order, scrolling past
// about four. Before the festival it shows the first night whole. "Full schedule" is the one way to the Sets page.
//
// The rows are NOT buttons (Sam, 2026-10-07): the card opens under the finger that tapped the stage, so that same tap
// landed on a row and went to Sets - every time the stage was tapped. Each row shows how many of your group picked it.
// The lineup is merged by performance first (a b2b is one row, with whichever member's photo exists).
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ArtistRow from '../lineup/ArtistRow';
import { stageNight, setProgress } from '../../lib/map/stageSchedule';
import { rowSizeForArtist } from '../../utils/lineupFormat';
import { mergePerformances, mergeVotersByPerformance } from '../../utils/performanceGroups';
import { useCurrentUserProfile } from '../../contexts/CurrentUserProfileContext';
import { useMyEventGroups, useEventGroupVoters } from '../../hooks/queries/useEventGroupVoters';

function nightLabel(day, nowMs, started) {
  const d = new Date(`${day}T12:00:00`);
  const today = new Date(nowMs);
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay || started) return 'Tonight'; // past midnight the night is still "tonight"
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function StageSetsPreview({ eventId, stage, lineup, nowMs }) {
  const navigate = useNavigate();
  const merged = useMemo(() => mergePerformances(lineup ?? []), [lineup]);
  const night = useMemo(() => stageNight(merged, stage, nowMs), [merged, stage, nowMs]);
  // Your group's picks, as the Sets page counts them (the first of your groups for this event).
  const { session, authReady } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  const { data: groups = [] } = useMyEventGroups(eventId, userId, { authReady });
  const { votersByArtist: rawVoters, totalMemberCount } = useEventGroupVoters({
    eventId,
    groupId: groups[0]?.id ?? null,
    userId,
  });
  const voters = useMemo(() => mergeVotersByPerformance(rawVoters, merged), [rawVoters, merged]);
  const withGroup = groups.length > 0 && totalMemberCount > 1;
  // An all-weekend stage (no days, no times: the Sunflower Sound System) has no night to show - its artists instead,
  // under "All weekend", the way the Sets page lists them (Sam, 2026-10-07).
  const allWeekend = useMemo(
    () =>
      night
        ? []
        : merged.filter((r) => r.stage === stage && !r.performance_day && !r.set_start_time),
    [night, merged, stage],
  );
  const open = () => navigate(`/sets/${encodeURIComponent(eventId)}`);
  if (!night && allWeekend.length) {
    return (
      <div className="stage-sets" role="region" aria-label={`${stage} artists`}>
        <div className="stage-sets__head">
          <span className="stage-sets__when">All weekend</span>
          <button type="button" className="stage-sets__all" onClick={open}>
            Full schedule
          </button>
        </div>
        <div className="stage-sets__list">
          {allWeekend.map((row) => (
            <div
              key={`allweekend|${row.artist_id ?? row.artist_name}`}
              className="stage-sets__item"
            >
              <ArtistRow
                artist={{ ...row, stage: null }}
                size={rowSizeForArtist(row)}
                layout="view"
                voters={withGroup ? voters?.[row.artist_id] : undefined}
                totalVoters={withGroup ? totalMemberCount : undefined}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (!night) return null;
  return (
    <div className="stage-sets" role="region" aria-label={`${stage} sets`}>
      <div className="stage-sets__head">
        <span className="stage-sets__when">
          {nightLabel(night.day, nowMs, night.sets[0].startMs <= nowMs)}
        </span>
        <button type="button" className="stage-sets__all" onClick={open}>
          Full schedule
        </button>
      </div>
      <div className="stage-sets__list">
        {night.sets.map(({ row, startMs, endMs, live }) => (
          <div
            key={`${row.artist_id ?? row.artist_name}|${startMs}`}
            className={`stage-sets__item${live ? ' stage-sets__item--live' : ''}`}
          >
            <ArtistRow
              artist={{ ...row, stage: null }}
              size={rowSizeForArtist(row)}
              layout="view"
              showTime
              voters={withGroup ? voters?.[row.artist_id] : undefined}
              totalVoters={withGroup ? totalMemberCount : undefined}
            />
            {live && (
              <span className="stage-sets__now">
                <span className="stage-sets__now-label">On now</span>
                <span className="stage-sets__track">
                  <span
                    className="stage-sets__fill"
                    style={{
                      width: `${Math.round(setProgress({ name: '', startMs, endMs }, nowMs) * 100)}%`,
                    }}
                  />
                </span>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
